<template>
  <VSearchableDropdown
    v-model="proxyModelValue"
    :class="variantClassName"
    :arrow-icon-class-name="arrowIconClassName"
    label-key="name"
    value-key="id"
    panel-width="350px"
    floating
    floating-strategy="absolute"
    :search-placeholder="`Search ${label.toLowerCase()}...`"
    :no-results-text="`No ${label.toLowerCase()} found`"
    :label="resolvedLabel"
    :placeholder="placeholder"
    :multiple="multiple"
    :searchable="true"
    :close-on-select="closeOnSelect"
    :fetch-items="fetchItems"
    :fetch-items-by-ids="fetchItemsByIds"
    :fetch-all-items="fetchAllItems"
    :show-select-all="showSelectAll"
    :selected-text-formatter="selectedTextFormatter"
  />
</template>

<script setup lang="ts" generic="Patch extends { id: number, name: string }">
  /**
   * PatchInput - A searchable dropdown for selecting patches
   *
   * Supports two modes:
   * - Mode 1 (dataClient): Server-side search, pagination, and fetching via data client
   * - Mode 2 (patches prop): Static list with client-side filtering only
   *   - Used when patches are pre-loaded (e.g., from auth store)
   *   - No server requests, no pagination
   */

  // Types
  import type { ComputedRef, WritableComputedRef } from 'vue';
  import type { TPatch, TPatchDataClient, TPaginatedPatches } from '~/misc/patch/types';
  import type {
    IVSearchableDropdownFetchResult,
    TVSearchableDropdownFetchFn,
    TVSearchableDropdownFetchByIdsFn,
    TVSearchableDropdownSelectedTextFormatter,
  } from '~/misc/v-searchable-dropdown/types';

  // Components
  import VSearchableDropdown from '~/misc/v-searchable-dropdown';

  const props = defineProps({
    patches: {
      type: Array as PropType<Patch[]>,
      default: undefined,
    },
    dataClient: {
      type: Object as PropType<TPatchDataClient>,
      default: undefined,
    },
    modelValue: {
      type: [Number, String, Array] as PropType<number | string | (number | string)[] | undefined>,
      default: undefined,
    },
    label: {
      type: String,
      default: 'Areas',
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    variantClassName: {
      type: String,
      default: '',
    },
    arrowIconClassName: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    closeOnSelect: {
      type: Boolean,
      default: false,
    },
    showSelectAll: {
      type: Boolean,
      default: true,
    },
    showSelectedTotal: {
      type: Boolean,
      default: false,
    },
    fetchItemsByIds: {
      type: Function as PropType<TVSearchableDropdownFetchByIdsFn<Patch>>,
      default: undefined,
    },
    // Optional: provided by the consumer to make "Select all" cover the full
    // matching set across all pages. When omitted, the dropdown falls back to
    // selecting only the pages currently in view.
    fetchAllItems: {
      type: Function as PropType<(query: string) => Promise<Patch[]>>,
      default: undefined,
    },
    assignedToMe: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits<{
    (e: 'update:modelValue', value: number | string | (number | string)[] | undefined): void;
  }>();

  // When a placeholder is provided, suppress the floating label so the dropdown
  // shows the placeholder text instead (the visible label is rendered externally).
  const resolvedLabel: ComputedRef<string> = computed<string>(() =>
    props.placeholder !== undefined ? '' : props.label
  );

  // When enabled (multi-select only), show "${count} selected of ${total}",
  // falling back to the dropdown's default "${count} selected" when the total
  // is unknown or the feature is off.
  const selectedTextFormatter: ComputedRef<TVSearchableDropdownSelectedTextFormatter | undefined> =
    computed<TVSearchableDropdownSelectedTextFormatter | undefined>(() =>
      props.showSelectedTotal
        ? (count: number, total: number | null): string =>
            total != null ? `${count} selected of ${total}` : `${count} selected`
        : undefined
    );

  const proxyModelValue: WritableComputedRef<number | string | (number | string)[] | undefined> =
    computed<number | string | (number | string)[] | undefined>({
      get(): number | string | (number | string)[] | undefined {
        return props.modelValue;
      },
      set(value: number | string | (number | string)[] | undefined): void {
        emit('update:modelValue', value);
      },
    });

  const fetchItems: TVSearchableDropdownFetchFn = async (
    query: string,
    page: number
  ): Promise<IVSearchableDropdownFetchResult<Patch | TPatch>> => {
    // Mode 1: Using dataClient
    if (props.dataClient) {
      const response: TPaginatedPatches = await props.dataClient.request<TPaginatedPatches>(
        'index',
        {
          page: page + 1,
          q: query,
          assigned_to_me: props.assignedToMe ? 1 : 0,
        }
      );

      return {
        items: response.data,
        hasMore: response.meta.current_page < response.meta.last_page,
        total: response.meta.total,
      };
    }

    // Mode 2: Using direct patches prop (static list with client-side filtering)
    if (props.patches !== undefined) {
      // Filter patches by search query (client-side)
      const filteredPatches: Patch[] = query
        ? props.patches.filter((patch: Patch) =>
            patch.name.toLowerCase().includes(query.toLowerCase())
          )
        : props.patches;

      return {
        items: filteredPatches,
        hasMore: false,
        total: filteredPatches.length,
      };
    }

    // Fallback: return empty result
    return {
      items: [],
      hasMore: false,
    };
  };
</script>
